import { useState, useRef, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { VoiceTestClient } from '@/utils/VoiceTestClient';
import { Phone, PhoneOff, Mic, Volume2, Wrench, CheckCircle, AlertTriangle, ChevronDown, ChevronUp, CreditCard, MessageSquare, User, Bot, Brain, SlidersHorizontal } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { analyzePrompt } from '@/lib/prompt-template';
import { isSearchWithoutSources, NO_SOURCES_TEXT } from '@/utils/google-search-card';

type ConversationItem = 
  | { type: 'transcript'; id: string; role: 'user' | 'agent'; text: string; timestamp: Date }
  | { type: 'tool_call'; id: string; toolName: string; args: any; timestamp: Date }
  | { type: 'tool_response'; id: string; toolName: string; response: any; timestamp: Date }
  | { type: 'tool_error'; id: string; toolName: string; error: string; timestamp: Date };

interface TestAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: string;
  agentName: string;
  systemPrompt?: string;
}

export function TestAgentDialog({ open, onOpenChange, agentId, agentName, systemPrompt }: TestAgentDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentWorkspaceId } = useWorkspace();
  const [isConnected, setIsConnected] = useState(false);
  const [conversation, setConversation] = useState<ConversationItem[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingText, setThinkingText] = useState('');
  const clientRef = useRef<VoiceTestClient | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [dynamicVariables, setDynamicVariables] = useState<Record<string, string>>({});

  const promptVariables = useMemo(
    () => analyzePrompt(systemPrompt ?? '').variables,
    [systemPrompt],
  );

  // Seed / reconcile variable state when the set of placeholders changes.
  useEffect(() => {
    setDynamicVariables((prev) => {
      const next: Record<string, string> = {};
      for (const name of promptVariables) {
        next[name] = prev[name] ?? '';
      }
      return next;
    });
  }, [promptVariables]);
  // Tracks recently seen transcripts: key = "role:text", value = Date.now() when first seen.
  // Skips duplicates that arrive within 3 seconds — handles backend sending same transcript
  // twice with slightly different timestamps.
  const seenTranscriptsRef = useRef<Map<string, number>>(new Map());

  // Auto-scroll to bottom when conversation or thinking state updates
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation, isThinking, thinkingText]);

  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Clears the transcript/tool-log so the dialog returns to the pre-call
  // "Set call variables" view. Must run on every disconnect path — manual
  // End Call, the bot ending the call itself (call_ended), or Daily dropping
  // the meeting — otherwise the previous call's transcript stays frozen on
  // screen underneath the variables panel.
  const resetConversationState = () => {
    setConversation([]);
    setIsThinking(false);
    setThinkingText('');
    seenTranscriptsRef.current.clear();
  };

  const buildVoiceClient = () => new VoiceTestClient(
    (role, text, timestamp) => {
      // Deduplicate: backend sometimes sends the same transcript twice with
      // slightly different timestamps. Skip if same role+text seen within 3s.
      const key = `${role}:${text}`;
      const now = Date.now();
      const lastSeen = seenTranscriptsRef.current.get(key);
      if (lastSeen && now - lastSeen < 3000) return;
      seenTranscriptsRef.current.set(key, now);

      setConversation(prev => [...prev, {
        type: 'transcript',
        id: `transcript-${Date.now()}-${Math.random()}`,
        role,
        text,
        timestamp: timestamp || new Date()
      }]);
    },
    (toolName, args) => {
      setConversation(prev => [...prev, {
        type: 'tool_call',
        id: `tool-call-${Date.now()}-${Math.random()}`,
        toolName,
        args,
        timestamp: new Date()
      }]);
    },
    (toolName, response) => {
      setConversation(prev => [...prev, {
        type: 'tool_response',
        id: `tool-response-${Date.now()}-${Math.random()}`,
        toolName,
        response,
        timestamp: new Date()
      }]);
    },
    (toolName, error) => {
      setConversation(prev => [...prev, {
        type: 'tool_error',
        id: `tool-error-${Date.now()}-${Math.random()}`,
        toolName,
        error,
        timestamp: new Date()
      }]);
    },
    (error) => {
      toast({
        title: 'Error',
        description: error,
        variant: 'destructive'
      });
    },
    (connected) => {
      setIsConnected(connected);
      if (!connected) {
        resetConversationState();
        toast({
          title: 'Disconnected',
          description: 'Call ended'
        });
      }
    },
    undefined, // onCallEnded
    () => { setIsThinking(true); setThinkingText(''); },        // onThinkingStart
    (content) => setThinkingText(prev => prev + content),        // onThinkingText
    () => setIsThinking(false),                                  // onThinkingEnd
  );

  // Pre-warm the Daily call object + mic permission as soon as the dialog opens
  // so the eventual Call click skips ~450ms of bundle download and the mic prompt.
  useEffect(() => {
    if (!open || !agentId) return;

    const client = buildVoiceClient();
    clientRef.current = client;
    client.prewarm().catch(() => {
      // Swallowed: connect() will surface real errors at click time
    });

    return () => {
      client.disconnect();
      if (clientRef.current === client) {
        clientRef.current = null;
      }
    };
  // buildVoiceClient closes over stable state setters and the toast hook,
  // so we intentionally only re-run on open/agentId.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId]);

  const handleConnect = async () => {
    if (!agentId) return;

    setIsConnecting(true);

    try {
      // The dialog-open effect has already created and prewarmed the client.
      // Defensive fallback: if it's somehow gone (e.g. a previous error path
      // nulled it), recreate one.
      if (!clientRef.current) {
        clientRef.current = buildVoiceClient();
      }
      const client = clientRef.current;

      // Drop empty fields so bot.py leaves the `{{placeholder}}` in place
      // rather than substituting an empty string — matches outbound behavior.
      const varsToSend: Record<string, string> = {};
      for (const [key, value] of Object.entries(dynamicVariables)) {
        const trimmed = value.trim();
        if (trimmed) varsToSend[key] = trimmed;
      }

      await client.connect(
        agentId,
        Object.keys(varsToSend).length > 0 ? varsToSend : undefined,
      );

      toast({
        title: 'Connected',
        description: 'You can now start speaking'
      });

      setIsConnecting(false);
    } catch (error: any) {
      setIsConnecting(false);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast({
          title: 'Permission Denied',
          description: 'Microphone access is required to test the agent',
          variant: 'destructive'
        });
      } else {
        // Check for insufficient balance error
        const errorMessage = error.message || 'Failed to connect to voice server';
        const isInsufficientBalance = errorMessage.toLowerCase().includes('top up') || 
                                       errorMessage.toLowerCase().includes('insufficient');
        
        toast({
          title: isInsufficientBalance ? 'Insufficient Balance' : 'Connection Failed',
          description: (
            <div className="flex flex-col gap-2">
              <span>{errorMessage}</span>
              {isInsufficientBalance && currentWorkspaceId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-fit mt-1 border-transparent text-foreground shadow-sm"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(`/${currentWorkspaceId}/billing`);
                  }}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Go to Billing
                </Button>
              )}
            </div>
          ),
          variant: 'destructive',
          duration: isInsufficientBalance ? 10000 : 5000,
        });
      }
    }
  };

  const handleDisconnect = () => {
    if (clientRef.current) {
      clientRef.current.disconnect();
      // Keep clientRef alive so the user can call again within the same dialog
      // session without paying the prewarm cost. The dialog-open effect's
      // cleanup will null it out when the dialog actually closes.
      setIsConnected(false);
      resetConversationState();
    }
  };

  const handleClose = () => {
    handleDisconnect();
    setDynamicVariables({});
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl h-[85vh] max-h-[720px] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-medium">{agentName.charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <div className="text-lg font-semibold">{agentName}</div>
              <Badge variant={isConnected ? "default" : "secondary"} className="mt-1">
                {isConnected ? "Call in Progress" : "Ready to Call"}
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-4 min-h-0">
          {!isConnected && promptVariables.length > 0 && (
            <div className="flex max-h-[300px] flex-col rounded-lg border bg-muted/30 px-4 py-3.5">
              <div className="mb-3 flex flex-shrink-0 items-center gap-2 text-sm font-medium">
                <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                <span>Set call variables</span>
                <Badge
                  variant="secondary"
                  className="ml-0.5 h-5 bg-primary/15 px-2 text-[10px] font-semibold uppercase tracking-wider text-primary"
                >
                  {promptVariables.length}
                </Badge>
              </div>
              <div className="grid min-h-0 flex-1 grid-cols-[minmax(8rem,auto)_1fr] items-center gap-x-4 gap-y-2 overflow-y-auto pr-1">
                {promptVariables.map((name) => (
                  <div key={name} className="contents">
                    <span className="truncate text-[11px] font-medium uppercase leading-none tracking-wider text-muted-foreground">
                      {name}
                    </span>
                    <Input
                      value={dynamicVariables[name] ?? ''}
                      onChange={(e) =>
                        setDynamicVariables((prev) => ({ ...prev, [name]: e.target.value }))
                      }
                      placeholder={`Value for {{${name}}}`}
                      className="h-8 text-sm"
                    />
                  </div>
                ))}
              </div>
              <p className="mt-2.5 flex-shrink-0 text-xs text-muted-foreground">
                Leave blank to simulate the value not being supplied on a real call.
              </p>
            </div>
          )}

          {/* Conversation & Tool Log Area */}
          <div ref={scrollRef} className="flex-1 border rounded-lg p-4 bg-muted/30 overflow-y-auto">
            {conversation.length === 0 && !isThinking ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <MessageSquare className="w-12 h-12 mb-4 opacity-50" />
                <p className="text-center font-medium mb-1">
                  {isConnected
                    ? "Listening..."
                    : "Click the Call button to start testing"}
                </p>
                <p className="text-center text-sm opacity-70">
                  {isConnected
                    ? "Conversation transcript will appear here"
                    : "Transcript and tool logs will appear here during the call"}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {conversation.map((item) => {
                  if (item.type === 'transcript') {
                    const isUser = item.role === 'user';
                    return (
                      <div key={item.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                        <div className={`flex items-start gap-2 max-w-[80%] ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isUser
                              ? 'bg-primary/10 text-primary'
                              : 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                          }`}>
                            {isUser ? <User className="w-3.5 h-3.5" /> : <Bot className="w-3.5 h-3.5" />}
                          </div>
                          <div>
                            <div className={`rounded-2xl px-3.5 py-2 ${
                              isUser
                                ? 'bg-primary text-primary-foreground rounded-tr-sm'
                                : 'bg-card border rounded-tl-sm'
                            }`}>
                              <p className="text-sm leading-relaxed">{item.text}</p>
                            </div>
                            <span className={`text-[10px] text-muted-foreground mt-0.5 block ${isUser ? 'text-right' : 'text-left'}`}>
                              {item.timestamp.toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (item.type === 'tool_call') {
                    const isExpanded = expandedItems.has(item.id);
                    return (
                      <div key={item.id} className="mx-auto max-w-[90%]">
                        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 p-3">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                              <Wrench className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="font-medium text-sm text-amber-900 dark:text-amber-100">
                                  Calling {item.toolName}
                                </p>
                                <button
                                  onClick={() => toggleExpanded(item.id)}
                                  className="text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300"
                                >
                                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>
                              </div>
                              {isExpanded && (
                                <div className="mt-2 p-2 rounded bg-amber-100/50 dark:bg-amber-900/20">
                                  <pre className="text-xs text-amber-900 dark:text-amber-100 whitespace-pre-wrap break-all font-mono">
                                    {JSON.stringify(item.args, null, 2)}
                                  </pre>
                                </div>
                              )}
                              <span className="text-xs text-amber-600 dark:text-amber-400 mt-1 block">
                                {item.timestamp.toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (item.type === 'tool_response') {
                    const isExpanded = expandedItems.has(item.id);
                    return (
                      <div key={item.id} className="mx-auto max-w-[90%]">
                        <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800 p-3">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className="font-medium text-sm text-green-900 dark:text-green-100">
                                  Response from {item.toolName}
                                </p>
                                <button
                                  onClick={() => toggleExpanded(item.id)}
                                  className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                                >
                                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>
                              </div>
                              {isExpanded && (
                                <div className="mt-2 p-2 rounded bg-green-100/50 dark:bg-green-900/20">
                                  <pre className="text-xs text-green-900 dark:text-green-100 whitespace-pre-wrap break-all font-mono">
                                    {isSearchWithoutSources(item.toolName, item.response) ? NO_SOURCES_TEXT : JSON.stringify(item.response, null, 2)}
                                  </pre>
                                </div>
                              )}
                              <span className="text-xs text-green-600 dark:text-green-400 mt-1 block">
                                {item.timestamp.toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (item.type === 'tool_error') {
                    return (
                      <div key={item.id} className="mx-auto max-w-[90%]">
                        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 p-3">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-red-900 dark:text-red-100">
                                Error in {item.toolName}
                              </p>
                              <p className="text-xs text-red-700 dark:text-red-300 mt-1 break-words">
                                {item.error}
                              </p>
                              <span className="text-xs text-red-600 dark:text-red-400 mt-1 block">
                                {item.timestamp.toLocaleTimeString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return null;
                })}

                {/* Thinking indicator */}
                {isThinking && (
                  <div className="flex justify-start">
                    <div className="flex items-start gap-2 max-w-[80%]">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400">
                        <Brain className="w-3.5 h-3.5" />
                      </div>
                      <div className="rounded-2xl px-3.5 py-2 bg-card border rounded-tl-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-muted-foreground">Thinking</span>
                          <span className="flex gap-0.5 items-center">
                            <span className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.3s]" />
                            <span className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce [animation-delay:-0.15s]" />
                            <span className="w-1 h-1 bg-muted-foreground rounded-full animate-bounce" />
                          </span>
                        </div>
                        {thinkingText && (
                          <p className="text-xs text-muted-foreground/70 mt-1 italic leading-relaxed line-clamp-2">
                            {thinkingText}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between gap-4 p-4 border rounded-lg bg-card">
            <div className="flex items-center gap-3">
              {isConnected ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm text-muted-foreground">Connected</span>
                  </div>
                  <Volume2 className="w-4 h-4 text-muted-foreground" />
                </>
              ) : (
                <span className="text-sm text-muted-foreground">Ready to call</span>
              )}
            </div>
            
            <div className="flex gap-2">
              {!isConnected ? (
                <Button
                  size="lg"
                  onClick={handleConnect}
                  disabled={isConnecting}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {isConnecting ? 'Connecting...' : 'Call'}
                </Button>
              ) : (
                <Button
                  size="lg"
                  variant="destructive"
                  onClick={handleDisconnect}
                >
                  <PhoneOff className="w-4 h-4 mr-2" />
                  End Call
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
